import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate as useNavHook, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Conversion attribution — persist utm_source from any landing URL so it
// survives navigation to /register (lead links look like ?utm_source=lead_xxx).
if (typeof window !== 'undefined') {
  try {
    const utm = new URLSearchParams(window.location.search).get('utm_source');
    if (utm) localStorage.setItem('hibff_utm_source', utm);
  } catch (e) { /* */ }
}

// Capacitor live updates — notifyAppReady() tells the Capgo updater the app booted successfully
// so it promotes the newly downloaded bundle to active. Without this call (within 10s of launch)
// the plugin rolls back to the previous bundle — protects us from shipping a broken bundle.
if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
  import('@capgo/capacitor-updater').then(({ CapacitorUpdater }) => {
    CapacitorUpdater.notifyAppReady().catch(() => {});
  }).catch(() => {});
}

// Tiny component that immediately forwards to the private admin login.
// Gives us a short, memorable URL (hibff.com/hq) without exposing React
// anywhere near the actual authentication flow — the redirect lands on
// a plain backend HTML page that has no rrweb/monkey-patching surface.
function HqRedirect() {
  React.useEffect(() => {
    window.location.replace('/api/auth/admin-direct');
  }, []);
  return null;
}
function RedirectWithQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import ParentRoute from './components/ParentRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import PublicRoute from './components/PublicRoute';
import DemoShell from './components/DemoShell';
import Homepage from './pages/Homepage';
import TrustPage from './pages/TrustPage';
import DocsPage from './pages/DocsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import PeerInviteLanding from './pages/PeerInviteLanding';
import ParentOnboarding from './pages/ParentOnboarding';
import ParentApproveHearts from './pages/ParentApproveHearts';
import ParentUpgradeApprove from './pages/ParentUpgradeApprove';
import TeenCharterSign from './pages/TeenCharterSign';
import SubscriptionRenewal from './pages/SubscriptionRenewal';
import AnniversaryReunion from './pages/AnniversaryReunion';
import PulseMuted from './pages/PulseMuted';
import EmailVerification from './pages/EmailVerification';
import WaitingForParent from './pages/WaitingForParent';
import ParentSetup from './pages/ParentSetup';
import Dashboard from './pages/Dashboard';
import AvALibrary from './pages/AvALibrary';
import ConversationsChat from './pages/ConversationsChat';
import MyReferences from './pages/MyReferences';
import Groups from './pages/Groups';
import Marketplace from './pages/Marketplace';
import Wellness from './pages/Wellness';
import Achievements from './pages/Achievements';
import RewardsReferrals from './pages/RewardsReferrals';
import HeartsStore from './pages/HeartsStore';
import Settings from './pages/Settings';
import ParentControls from './pages/ParentControls';
import ParentDashboard from './pages/ParentDashboard';
import CustomParentDashboard from './pages/CustomParentDashboard';
import ParentUpgradePlan from './pages/ParentUpgradePlan';
import ParentHeartsStore from './pages/ParentHeartsStore';
import ParentTeenVaultPage from './pages/ParentTeenVaultPage';
import ParentDocsPage from './pages/ParentDocsPage';
import ParentHelpPage from './pages/ParentHelpPage';
import ParentProgressPage from './pages/ParentProgressPage';
import ParentChallengeJourney from './pages/ParentChallengeJourney';
import NFTListingPage from './pages/NFTListingPage';
import ChildSafetyPage from './pages/ChildSafetyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import MagicLoginRedirect from './pages/MagicLoginRedirect';
import ParentFirstSignup from './pages/onboarding/ParentFirstSignup';
import ParentConsole from './pages/onboarding/ParentConsole';
import ParentRegisterPasswordless from './pages/onboarding/ParentRegisterPasswordless';
import ParentLoginTotp from './pages/onboarding/ParentLoginTotp';
import CancelSubscription from './pages/CancelSubscription';
import DeactivateAccount from './pages/DeactivateAccount';
import Help from './pages/Help';
import PricingPage from './pages/PricingPage';
import BillingSuccess from './pages/BillingSuccess';
import CharterPage from './pages/CharterPage';
import BadgesPage from './pages/BadgesPage';
import ChallengesPage from './pages/ChallengesPage';
import WeeklyReport from './pages/WeeklyReport';
import NFTMarketplace from './pages/NFTMarketplace';
import NotFound from './pages/NotFound';
import QuestsPage from './pages/QuestsPage';
import OnboardingFlow from './pages/OnboardingFlow';
import InvestorOpportunity from './pages/InvestorOpportunity';
import InvestorPortal from './pages/InvestorPortal';
import Partners from './pages/Partners';
import AdminHome from './pages/admin/AdminHome';
import GlobalAnalytics from './pages/admin/GlobalAnalytics';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import AdminUsers from './pages/admin/AdminUsers';
import AdminHelpRequests from './pages/admin/AdminHelpRequests';
import AdminBugReports from './pages/admin/AdminBugReports';
import AdminMaintenance from './pages/admin/AdminMaintenance';
import AdminNFTEconomy from './pages/admin/AdminNFTEconomy';
import AdminOpsAlerts from './pages/admin/AdminOpsAlerts';
import AdminRevenue from './pages/admin/AdminRevenue';
import AdminSafety from './pages/admin/AdminSafety';
import AdminTrustSafety from './pages/admin/AdminTrustSafety';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminReports from './pages/admin/AdminReports';
import AdminWaitlistBroadcast from './pages/admin/AdminWaitlistBroadcast';
import AdminLeads from './pages/admin/AdminLeads';
import AdminWelcomePackLeads from './pages/admin/AdminWelcomePackLeads';
import AdminPartnerLeads from './pages/admin/AdminPartnerLeads';
import AdminSettings from './pages/admin/AdminSettings';
import AdminTeam from './pages/admin/AdminTeam';
import SetupPassword from './pages/SetupPassword';
import AccessibilityPage from './pages/AccessibilityPage';
import StatusPage from './pages/StatusPage';
import StatusHistory from './pages/StatusHistory';
import TrustCenter from './pages/TrustCenter';
import SubprocessorsPage from './pages/SubprocessorsPage';
import SLAPage from './pages/SLAPage';
import PostmortemsPage from './pages/PostmortemsPage';
import ContactPage from './pages/ContactPage';
import AdminAudit from './pages/admin/AdminAudit';
import AdminDemoCodes from './pages/admin/AdminDemoCodes';
import SecureVault from './pages/admin/SecureVault';
import VaultExport from './pages/admin/VaultExport';
import SchoolsCommandCentre from './pages/admin/SchoolsCommandCentre';
import DataRoom from './pages/DataRoom';
import AdminContent from './pages/admin/AdminContent';
import AdminFeatureFlags from './pages/admin/AdminFeatureFlags';
import AdminSchools from './pages/admin/AdminSchools';
import AdminCorporates from './pages/admin/AdminCorporates';
import AdminDeveloperSupport from './pages/admin/AdminDeveloperSupport';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminSecrets from './pages/admin/AdminSecrets';
import AdminLogs from './pages/admin/AdminLogs';
import DeveloperDashboard from './pages/developer/DeveloperDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import LegalDocPage from './pages/LegalDocPage';
import TeenSafety from './pages/TeenSafety';
import TrustSafety from './pages/TrustSafety';
import TeenInviteLanding from './pages/TeenInviteLanding';
import { PathwayCreate, PathwayDetail, PathwayList } from './pages/Pathways';
import { CohortJourneyDashboard, JourneyList } from './pages/CohortJourney';
import FocusPicker from './pages/FocusPicker';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import MemeGallery from './pages/MemeGallery';
import FriendChallenges from './pages/FriendChallenges';
import ChallengeLeaderboard from './pages/ChallengeLeaderboard';
import FriendMeetups from './pages/FriendMeetups';
import CommunityPage from './pages/CommunityPage';
import PreLaunch from './pages/PreLaunch';
import StartAvA from './pages/StartAvA';
import AdminCampaign from './pages/admin/AdminCampaign';
import About from './pages/About';
import VerifyCertificate from './pages/VerifyCertificate';
import VaultDemo from './pages/VaultDemo';
import CollectibleShowcase from './pages/CollectibleShowcase';
import OceanProfile from './pages/OceanProfile';
import SchoolRegistration from './pages/school/SchoolRegistration';
import SchoolsLanding from './pages/SchoolsLanding';
import WarmHandoverRedirect from './pages/WarmHandoverRedirect';

import SchoolDashboard from './pages/school/SchoolDashboard';
import StudentPreview from './pages/school/StudentPreview';
import SchoolStudentDashboard from './pages/SchoolStudentDashboard';
import CorporateRegistration from './pages/corporate/CorporateRegistration';
import CorporateDashboard from './pages/corporate/CorporateDashboard';
import TeamMemberPreview from './pages/corporate/TeamMemberPreview';
import CorporateTeamMemberDashboard from './pages/CorporateTeamMemberDashboard';
import CookieConsent from './components/CookieConsent';
import AddToHomeScreen from './components/AddToHomeScreen';
import GrowthStory from './pages/GrowthStory';
import MoodPlaylist from './pages/MoodPlaylist';
import SkillTree from './pages/SkillTree';
import GrowthJournal from './pages/GrowthJournal';
import RolePlayScenarios from './pages/RolePlayScenarios';
import AccountabilityPods from './pages/AccountabilityPods';
import MentorMatching from './pages/MentorMatching';
import Shop from './pages/Shop';
import Documentation from './pages/Documentation';
import './styles/dashboard.css';
import './index.css';
import { initDeepLinkHandler } from './utils/capacitor';
import PageTelemetry from './components/PageTelemetry';
import NavigationSafetyNet from './components/NavigationSafetyNet';
import VerificationGateModal from './components/VerificationGateModal';
import AuthCallback from './pages/AuthCallback';

function DeepLinkInitializer() {
  const nav = useNavHook();
  React.useEffect(() => { initDeepLinkHandler(nav); }, [nav]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // NOTE: /auth/google is handled ONLY by its <Route> below. This component
  // previously ALSO rendered AuthCallback for that path, mounting two
  // instances side-by-side — the second instance re-fired the OAuth effect
  // mid-navigation and overwrote the single-use-code guard message (QA C-01).
  // Legacy Emergent OAuth fallback (kept for any in-flight redirects during
  // the cutover; AuthCallback gracefully handles missing session_id).
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return null;
}

function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
          <BrowserRouter>
          <Toaster position="top-center" richColors />
          <DeepLinkInitializer />
          <NavigationSafetyNet />
          <VerificationGateModal />
          <PageTelemetry />
          <AnalyticsTracker />
          <AppRouter />
          <DemoShell>
          <Routes>
            {/* Public Homepage - shows for non-authenticated users */}
            <Route path="/" element={<PublicRoute><Homepage /></PublicRoute>} />
            {/* Clean-URL section routes — menu tabs match the address bar (no # fragments) */}
            <Route path="/how-it-works" element={<PublicRoute><Homepage /></PublicRoute>} />
            <Route path="/why-hibff" element={<PublicRoute><Homepage /></PublicRoute>} />
            <Route path="/safety" element={<PublicRoute><Homepage /></PublicRoute>} />
            <Route path="/teens-parents" element={<PublicRoute><Homepage /></PublicRoute>} />
            <Route path="/adults" element={<PublicRoute><Homepage /></PublicRoute>} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/:slug" element={<DocsPage />} />
            <Route path="/trust-center" element={<TrustCenter />} />
            <Route path="/subprocessors" element={<SubprocessorsPage />} />
            <Route path="/sla" element={<SLAPage />} />
            <Route path="/postmortems" element={<PostmortemsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/status/history" element={<StatusHistory />} />
            <Route path="/system-status" element={<StatusPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/setup-password" element={<SetupPassword />} />

            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/adult" element={<Register />} />
            <Route path="/join/:code" element={<PeerInviteLanding />} />
            <Route path="/parent-onboarding" element={<ParentOnboarding />} />
            {/* Sprint 208A — Dual onboarding pipelines */}
            <Route path="/parent-first" element={<ParentFirstSignup />} />
            <Route path="/parent-console/:token" element={<ParentConsole />} />
            {/* Sprint 211_FINAL — Passwordless / PIN-less TOTP parent auth */}
            <Route path="/parent-register" element={<ParentRegisterPasswordless />} />
            <Route path="/parent-login" element={<ParentLoginTotp />} />
            <Route path="/parent-approve-hearts" element={<ParentApproveHearts />} />
            <Route path="/parent-approve-upgrade" element={<ParentUpgradeApprove />} />
            <Route path="/teen-charter-sign" element={<TeenCharterSign />} />
            <Route path="/subscription-renewal" element={<SubscriptionRenewal />} />
            <Route path="/anniversary-reunion" element={<AnniversaryReunion />} />
            <Route path="/pulse-muted" element={<PulseMuted />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/waiting-for-parent" element={<WaitingForParent />} />
            <Route path="/parent-setup" element={<ParentSetup />} />

            {/* Protected Routes - Dashboard and App */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/conversations" element={<ProtectedRoute><AvALibrary /></ProtectedRoute>} />
            <Route path="/conversations/chat" element={<ProtectedRoute><ConversationsChat /></ProtectedRoute>} />
            {/* Intuitive-path aliases (Oct 2026 mobile audit) — stop logged-in
                users being silently dumped on the marketing homepage. */}
            <Route path="/chat" element={<Navigate to="/conversations/chat" replace />} />
            <Route path="/wallet" element={<Navigate to="/hearts" replace />} />
            <Route path="/nft" element={<Navigate to="/nft-marketplace" replace />} />
            <Route path="/collectibles" element={<Navigate to="/nft-marketplace" replace />} />
            <Route path="/conversations/practice/:avaSlug" element={<ProtectedRoute><ConversationsChat /></ProtectedRoute>} />
            <Route path="/my-references" element={<ProtectedRoute><MyReferences /></ProtectedRoute>} />
            <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
            <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
            <Route path="/nft-marketplace" element={<ProtectedRoute><NFTMarketplace /></ProtectedRoute>} />
            <Route path="/quests" element={<ProtectedRoute><QuestsPage /></ProtectedRoute>} />
            <Route path="/wellness" element={<ProtectedRoute><Wellness /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><RewardsReferrals /></ProtectedRoute>} />
            <Route path="/hearts" element={<ProtectedRoute><HeartsStore /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/parent-controls" element={<ProtectedRoute><ParentControls /></ProtectedRoute>} />
            <Route path="/parent-dashboard" element={<ParentRoute><CustomParentDashboard /></ParentRoute>} />
            <Route path="/parent-upgrade" element={<ParentRoute><ParentUpgradePlan /></ParentRoute>} />
            <Route path="/parent-hearts" element={<ParentRoute><ParentHeartsStore /></ParentRoute>} />
            <Route path="/parent-teen-vault" element={<ParentRoute><ParentTeenVaultPage /></ParentRoute>} />
            <Route path="/parent-docs" element={<ParentRoute><ParentDocsPage /></ParentRoute>} />
            <Route path="/parent-help" element={<ParentRoute><ParentHelpPage /></ParentRoute>} />
            <Route path="/parent-progress" element={<ParentRoute><ParentProgressPage /></ParentRoute>} />
            <Route path="/parent-challenges" element={<ParentRoute><ParentChallengeJourney /></ParentRoute>} />
            <Route path="/nft-listing/:listingId" element={<NFTListingPage />} />
            <Route path="/parent-magic" element={<MagicLoginRedirect />} />
            <Route path="/parent-dashboard-legacy" element={<ParentRoute><ParentDashboard /></ParentRoute>} />
            <Route path="/charter" element={<ProtectedRoute><CharterPage /></ProtectedRoute>} />
            <Route path="/badges" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
            <Route path="/memes" element={<ProtectedRoute><MemeGallery /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><OceanProfile /></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
            <Route path="/friend-challenges" element={<ProtectedRoute><FriendChallenges /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><ChallengeLeaderboard /></ProtectedRoute>} />
            <Route path="/meetups" element={<ProtectedRoute><FriendMeetups /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
            <Route path="/growth-story" element={<ProtectedRoute><GrowthStory /></ProtectedRoute>} />
            <Route path="/mood-playlist" element={<ProtectedRoute><MoodPlaylist /></ProtectedRoute>} />
            <Route path="/skill-tree" element={<ProtectedRoute><SkillTree /></ProtectedRoute>} />
            <Route path="/growth-journal" element={<ProtectedRoute><GrowthJournal /></ProtectedRoute>} />
            <Route path="/role-play" element={<ProtectedRoute><RolePlayScenarios /></ProtectedRoute>} />
            <Route path="/accountability-pods" element={<ProtectedRoute><AccountabilityPods /></ProtectedRoute>} />
            <Route path="/mentorship" element={<ProtectedRoute><MentorMatching /></ProtectedRoute>} />
            <Route path="/weekly-report" element={<ProtectedRoute><WeeklyReport /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingFlow /></ProtectedRoute>} />
            <Route path="/partners" element={<ProtectedRoute><Partners /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminHome /></ProtectedRoute>} />
            <Route path="/admin/global-analytics" element={<ProtectedRoute><GlobalAnalytics /></ProtectedRoute>} />
            <Route path="/admin/campaign" element={<ProtectedRoute><AdminCampaign /></ProtectedRoute>} />
            <Route path="/hq" element={<HqRedirect />} />
            <Route path="/investor-portal" element={<InvestorOpportunity />} />
            <Route path="/investors" element={<RedirectWithQuery to="/investor-portal" />} />
            <Route path="/investor-vault" element={<InvestorPortal />} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/help-requests" element={<ProtectedRoute><AdminHelpRequests /></ProtectedRoute>} />
            <Route path="/admin/bug-reports" element={<ProtectedRoute><AdminBugReports /></ProtectedRoute>} />
            <Route path="/admin/maintenance" element={<ProtectedRoute><AdminMaintenance /></ProtectedRoute>} />
            <Route path="/admin/nft-economy" element={<ProtectedRoute><AdminNFTEconomy /></ProtectedRoute>} />
            <Route path="/admin/ops-alerts" element={<ProtectedRoute><AdminOpsAlerts /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
            <Route path="/admin/welcome-pack-leads" element={<ProtectedRoute><AdminWelcomePackLeads /></ProtectedRoute>} />
            <Route path="/admin/partner-leads" element={<ProtectedRoute><AdminPartnerLeads /></ProtectedRoute>} />
            <Route path="/admin/revenue" element={<ProtectedRoute><AdminRevenue /></ProtectedRoute>} />
            <Route path="/admin/safety" element={<ProtectedRoute><AdminSafety /></ProtectedRoute>} />
            <Route path="/admin/trust-safety" element={<ProtectedRoute><AdminTrustSafety /></ProtectedRoute>} />
            <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotifications /></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute><AdminReports /></ProtectedRoute>} />
            <Route path="/admin/waitlist-broadcast" element={<ProtectedRoute><AdminWaitlistBroadcast /></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
            <Route path="/admin/security" element={<ProtectedRoute><AdminSecurity /></ProtectedRoute>} />
            <Route path="/admin/secrets" element={<ProtectedRoute><AdminSecrets /></ProtectedRoute>} />
            <Route path="/admin/logs" element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
            <Route path="/admin/content" element={<ProtectedRoute><AdminContent /></ProtectedRoute>} />
            <Route path="/admin/features" element={<ProtectedRoute><AdminFeatureFlags /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
            <Route path="/admin/demo-codes" element={<ProtectedRoute><AdminDemoCodes /></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
            <Route path="/admin/vault" element={<ProtectedRoute><SecureVault /></ProtectedRoute>} />
            <Route path="/admin/secure-vault" element={<ProtectedRoute><SecureVault /></ProtectedRoute>} />
            <Route path="/admin/vault/export" element={<ProtectedRoute><VaultExport /></ProtectedRoute>} />
            <Route path="/admin/gives" element={<Navigate to="/admin/schools-engine" replace />} />
            <Route path="/admin/hibff-gives" element={<Navigate to="/admin/schools-engine" replace />} />
            <Route path="/admin/schools-engine" element={<ProtectedRoute><SchoolsCommandCentre /></ProtectedRoute>} />
            <Route path="/admin/schools-programme" element={<ProtectedRoute><SchoolsCommandCentre /></ProtectedRoute>} />
            <Route path="/admin/schools" element={<ProtectedRoute><AdminSchools /></ProtectedRoute>} />
            <Route path="/admin/corporates" element={<ProtectedRoute><AdminCorporates /></ProtectedRoute>} />
            <Route path="/admin/developer-support" element={<ProtectedRoute><AdminDeveloperSupport /></ProtectedRoute>} />
            <Route path="/developer" element={<ProtectedRoute><DeveloperDashboard /></ProtectedRoute>} />
            <Route path="/privacy" element={<LegalDocPage doc="privacy" />} />
            <Route path="/child-safety" element={<ChildSafetyPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/teen-safety" element={<TeenSafety />} />
            <Route path="/trust-safety" element={<TrustSafety />} />
            <Route path="/about" element={<About />} />
            <Route path="/gives" element={<Navigate to="/education" replace />} />
            <Route path="/hibff-gives" element={<Navigate to="/education" replace />} />
            <Route path="/HiBFF-Gives" element={<Navigate to="/education" replace />} />
            <Route path="/HiBFFGives" element={<Navigate to="/education" replace />} />
            <Route path="/mayor-portal" element={<Navigate to="/education" replace />} />
            <Route path="/mayor" element={<Navigate to="/education" replace />} />
            <Route path="/verify/:id" element={<VerifyCertificate />} />
            <Route path="/terms" element={<LegalDocPage doc="terms" />} />
            <Route path="/refund-policy" element={<LegalDocPage doc="refund" />} />
            <Route path="/pause-policy-90" element={<LegalDocPage doc="pause-90" />} />
            <Route path="/pause-policy-indefinite" element={<LegalDocPage doc="pause-indefinite" />} />
            <Route path="/teen-invite/:token" element={<TeenInviteLanding />} />
            <Route path="/pathways" element={<ProtectedRoute><PathwayList /></ProtectedRoute>} />
            <Route path="/pathways/create" element={<ProtectedRoute><PathwayCreate /></ProtectedRoute>} />
            <Route path="/pathways/:pathwayId" element={<ProtectedRoute><PathwayDetail /></ProtectedRoute>} />
            <Route path="/journeys" element={<ProtectedRoute><JourneyList /></ProtectedRoute>} />
            <Route path="/journeys/:journeyId" element={<ProtectedRoute><CohortJourneyDashboard /></ProtectedRoute>} />
            <Route path="/focus" element={<ProtectedRoute><FocusPicker /></ProtectedRoute>} />
            <Route path="/pre-launch" element={<PreLaunch />} />
            <Route path="/3-free-practice-sessions" element={<PreLaunch />} />
            <Route path="/start/:slug" element={<StartAvA />} />
            <Route path="/education" element={<SchoolsLanding />} />
            <Route path="/schools" element={<Navigate to="/education" replace />} />
            <Route path="/warm-handover" element={<WarmHandoverRedirect />} />
            <Route path="/school/register" element={<SchoolRegistration />} />
            <Route path="/vault-demo" element={<VaultDemo />} />
            <Route path="/dataroom" element={<DataRoom />} />
            <Route path="/showcase/:slug" element={<CollectibleShowcase />} />
            <Route path="/school" element={<ProtectedRoute><SchoolDashboard /></ProtectedRoute>} />
            <Route path="/school/student-preview" element={<ProtectedRoute><StudentPreview /></ProtectedRoute>} />
            <Route path="/school/student" element={<ProtectedRoute><SchoolStudentDashboard /></ProtectedRoute>} />
            <Route path="/corporate/register" element={<CorporateRegistration />} />
            <Route path="/corporate" element={<ProtectedRoute><CorporateDashboard /></ProtectedRoute>} />
            <Route path="/corporate/member-preview" element={<ProtectedRoute><TeamMemberPreview /></ProtectedRoute>} />
            <Route path="/corporate/member" element={<ProtectedRoute><CorporateTeamMemberDashboard /></ProtectedRoute>} />
            <Route path="/cancel-subscription" element={<ProtectedRoute><CancelSubscription /></ProtectedRoute>} />
            <Route path="/deactivate-account" element={<ProtectedRoute><DeactivateAccount /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/help/:categoryId" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/help/article/:articleId" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />

            <Route path="/shop" element={<Shop />} />
            <Route path="/docs" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
            <Route path="/dashboard/docs" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />

            {/* Catch all - redirect to homepage */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </DemoShell>
          <CookieConsent />
          <AddToHomeScreen />
        </BrowserRouter>
          </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
